import React, { useState, useEffect } from 'react';
import { CellPlugin } from '@react-page/editor';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import { ImageUploadFunction } from './customImagePlugin';

export interface CarouselImage {
    id: string;
    url: string;
    alt: string;
}

export interface CarouselData extends Record<string, unknown> {
    images?: CarouselImage[];
    autoPlay?: boolean;
    interval?: number;
}

export function createCarouselPlugin(
    uploadImage?: ImageUploadFunction
): CellPlugin<CarouselData> {
    return {
        id: 'carousel-plugin',
        title: 'Carousel',
        description: 'A slideshow of images',
        icon: <ViewCarouselIcon />,
        version: 1,

        Renderer: ({ data }) => {
            const [currentIndex, setCurrentIndex] = useState(0);
            const images = data.images || [];

            useEffect(() => {
                if (data.autoPlay && images.length > 1) {
                    const timer = setInterval(() => {
                        setCurrentIndex((prev) => (prev + 1) % images.length);
                    }, data.interval || 3000);
                    return () => clearInterval(timer);
                }
            }, [data.autoPlay, data.interval, images.length]);

            if (images.length === 0) {
                return <div className="carousel-placeholder">No images in carousel</div>;
            }

            const nextSlide = () => setCurrentIndex((prev) => (prev + 1) % images.length);
            const prevSlide = () => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);

            return (
                <div className="carousel-root">
                    <div className="carousel-container">
                        {images.map((img, idx) => (
                            <div
                                key={img.id}
                                className={`carousel-slide ${idx === currentIndex ? 'active' : ''}`}
                                style={{ display: idx === currentIndex ? 'block' : 'none' }}
                            >
                                <img src={img.url} alt={img.alt} />
                            </div>
                        ))}
                    </div>
                    {images.length > 1 && (
                        <>
                            <button className="carousel-prev" onClick={prevSlide}>❮</button>
                            <button className="carousel-next" onClick={nextSlide}>❯</button>
                            <div className="carousel-dots">
                                {images.map((_, idx) => (
                                    <span
                                        key={idx}
                                        className={`carousel-dot ${idx === currentIndex ? 'active' : ''}`}
                                        onClick={() => setCurrentIndex(idx)}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            );
        },

        controls: {
            type: 'custom',
            Component: ({ data, onChange }) => {
                const images = data.images || [];

                const handleBulkUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
                    const files = e.target.files;
                    if (!files || files.length === 0 || !uploadImage) return;

                    const newImages: CarouselImage[] = [...images];
                    for (const file of Array.from(files)) {
                        try {
                            const { url } = await uploadImage(file);
                            newImages.push({
                                id: Math.random().toString(36).substring(2, 11),
                                url,
                                alt: file.name
                            });
                        } catch (err) {
                            console.error("Upload failed", err);
                        }
                    }
                    onChange({ ...data, images: newImages });
                    e.target.value = '';
                };

                const removeImage = (id: string) => {
                    onChange({ ...data, images: images.filter(img => img.id !== id) });
                };

                const moveImage = (index: number, direction: 'up' | 'down') => {
                    const newImages = [...images];
                    const targetIndex = direction === 'up' ? index - 1 : index + 1;
                    if (targetIndex < 0 || targetIndex >= newImages.length) return;

                    [newImages[index], newImages[targetIndex]] = [newImages[targetIndex], newImages[index]];
                    onChange({ ...data, images: newImages });
                };

                return (
                    <div className="carousel-controls">
                        <h4>Carousel Settings</h4>
                        <label>
                            <input
                                type="checkbox"
                                checked={!!data.autoPlay}
                                onChange={(e) => onChange({ ...data, autoPlay: e.target.checked })}
                            />
                            Auto Play
                        </label>
                        <br />
                        <label>
                            Interval (ms):
                            <input
                                type="number"
                                value={data.interval || 3000}
                                onChange={(e) => onChange({ ...data, interval: parseInt(e.target.value) })}
                            />
                        </label>
                        <hr />
                        <h4>Images ({images.length})</h4>
                        <div className="carousel-admin-bulk">
                            <input
                                type="file"
                                multiple
                                accept="image/*"
                                onChange={handleBulkUpload}
                                id="carousel-bulk-input"
                                style={{ display: 'none' }}
                            />
                            <button onClick={() => document.getElementById('carousel-bulk-input')?.click()}>
                                Bulk Upload Images
                            </button>
                        </div>
                        <div className="carousel-image-list">
                            {images.map((img, idx) => (
                                <div key={img.id} className="carousel-admin-item">
                                    <img src={img.url} style={{ width: 50, height: 50, objectFit: 'cover' }} />
                                    <span>{img.alt}</span>
                                    <div className="carousel-item-actions">
                                        <button onClick={() => moveImage(idx, 'up')} disabled={idx === 0}>↑</button>
                                        <button onClick={() => moveImage(idx, 'down')} disabled={idx === images.length - 1}>↓</button>
                                        <button onClick={() => removeImage(img.id)} style={{ color: 'red' }}>✕</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            }
        }
    };
}
